import React, {
    Children,
    ComponentType,
    createContext,
    isValidElement,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import * as production from 'react/jsx-runtime';
import rehypeParse from 'rehype-parse';
import rehypeReact, { Options } from 'rehype-react';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { Pluggable, Processor, unified } from 'unified';

import { TVStyleProps } from '../../style-system';
import { Menu, type MenuItemProps } from '../menu';
import { ScrollArea } from '../scroll-area/scroll-area';

import { proseStyles } from './prose.styles';
import { useActiveHeading } from './prose.utils';
import { ProseImg, type ProseImgProps } from './prose-img';

type HeadingType = {
    id: string;
    children: React.ReactNode;
    href: string;
    indent: MenuItemProps['indent'];
};

const RenderContext = createContext<{
    parser: Processor<any, any, any, undefined, undefined>;
    content: string | Record<string, string>;
    id: string;
}>({
    parser: undefined!,
    content: undefined!,
    id: undefined!,
});

const LinkComponent = ({ href, children, ...props }: any) => {
    // Ensure the href starts with a protocol
    if (!href.includes('://')) {
        href = `https://${href}`;
    }
    return (
        <a href={href} {...props} target="_blank" rel="nofollow">
            {children}
        </a>
    );
};

const rehypeOptions = (components?: Options['components']) => {
    return {
        passNode: true,
        components: {
            a: LinkComponent,
            img: ProseImg,
            ...components,
        },
        ...production,
    } as Options;
};

const createCompiler = (isHtml?: boolean) => {
    const compiler = unified();

    if (isHtml) {
        compiler
            .use(rehypeParse, { fragment: true }) // Parse HTML fragments
            .use(rehypeSlug); // Add IDs to headings
    } else {
        compiler.use(remarkParse).use(remarkGfm).use(remarkRehype).use(rehypeSlug);
    }

    return compiler;
};
type SectionProps = TVStyleProps<typeof proseStyles> & {
    components?: Options['components'] & Record<string, ComponentType>;
    name?: string;
};

const Section = ({ name, size = 'sm', variant, components: overrides }: SectionProps) => {
    const { parser, content, id } = useContext(RenderContext);
    const contentTarget = typeof content === 'string' ? content : (content[name!] ?? '');

    const result = useMemo(
        () => parser().use(rehypeReact, rehypeOptions(overrides)).processSync(contentTarget).result,
        [parser, contentTarget],
    );

    if (!isValidElement(result)) return null;

    return (
        <div className={proseStyles({ size, variant })} data-prose={id}>
            {result}
        </div>
    );
};

const findText = (node: any): string => {
    if (!node) return '';

    if (typeof node === 'string') {
        return node;
    }

    if (Array.isArray(node)) {
        return node.map(findText).join('');
    }

    if (isValidElement(node)) {
        return findText((node.props as any)?.children);
    }

    return '';
};

const getHeadingFromNode = (node: JSX.Element): HeadingType | undefined => {
    if (!['h1', 'h2', 'h3'].includes(node?.type)) {
        return undefined;
    }

    const headingText = findText(node);
    const indentMap: Record<string, number> = {
        h1: 0,
        h2: 1,
        h3: 2,
    };
    const indent = indentMap[node.type as string] || 0;
    const id = node.props?.id;

    return {
        id,
        children: headingText,
        href: `#${id}`,
        indent: indent as MenuItemProps['indent'],
    };
};

const getHeadingsFromTree = (node: JSX.Element): HeadingType[] => {
    const headings: HeadingType[] = [];

    if (!isValidElement(node)) {
        return headings;
    }

    const heading = getHeadingFromNode(node);
    if (heading) {
        headings.push(heading);
    }

    const children = (node.props as any)?.children;

    if (children) {
        const childArray = Children.toArray(children);

        childArray.forEach(child => {
            if (isValidElement(child)) {
                const childHeadings = getHeadingsFromTree(child);
                headings.push(...childHeadings);
            }
        });
    }

    return headings;
};

const ToC = ({ autoSize, onClick, contentId }: { autoSize?: boolean; onClick?: () => void; contentId?: string }) => {
    const { parser, content, id: proseId } = useContext(RenderContext);
    const scrollRef = useRef<HTMLDivElement>(null);
    const fullDocument = useMemo(() => {
        if (typeof content === 'string') return content;
        return Object.values(content).join('\n');
    }, [content]);

    const getSize = (indent: number) => (autoSize && indent > 1 ? 'xs' : 'sm');

    const items = useMemo(() => {
        const tree = parser().use(rehypeReact, rehypeOptions()).processSync(fullDocument).result as JSX.Element;
        if (!isValidElement(tree)) return [];
        const headings = getHeadingsFromTree(tree);
        return headings;
    }, [parser, fullDocument]);

    const proseContentId = contentId ? `#${contentId}` : `[data-prose=${proseId}]`;
    const { activeId } = useActiveHeading(proseContentId, content);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const container = scrollRef.current;
        const navitem = document.getElementById(`toc-link-${activeId}`);

        if (container && navitem) {
            const containerHeight = container.getBoundingClientRect().height;
            const itemHeight = navitem.getBoundingClientRect().height;
            // Active item will be
            container.scrollTo({
                top: navitem.offsetTop - ((containerHeight / itemHeight) * itemHeight) / 3,
                behavior: 'smooth',
            });
        }
    }, [activeId]);

    return (
        <ScrollArea h="calc(90vh - 112px)" ref={scrollRef}>
            <Menu>
                <Menu.List>
                    {items.map(item => (
                        <Menu.Item
                            {...item}
                            size={getSize(item.indent ?? 0)}
                            key={item.id}
                            active={item.id === activeId}
                            id={`toc-link-${item.id}`}
                            indent={item.indent}
                            onClick={onClick}
                        />
                    ))}
                </Menu.List>
            </Menu>
        </ScrollArea>
    );
};

type ProseRootProps = PropsWithChildren<
    Pick<TVStyleProps<typeof proseStyles>, 'size'> & {
        plugins?: Pluggable[];
        content: string | Record<string, string>;
        id: string;
        html?: boolean;
    }
>;

const Root = ({ children, content, html, id, size }: ProseRootProps) => {
    const [parser] = useState(() => createCompiler(html));
    const headings = useRef([]);
    const ctx = useMemo(() => ({ parser, content, id, headings: headings.current }), [content, parser, id]);

    if (!children) {
        return (
            <RenderContext.Provider value={ctx}>
                {typeof content === 'string' && <Section size={size} />}
                {typeof content === 'object' &&
                    Object.keys(content).map(name => <Section key={`${id}-${name}`} name={name} size={size} />)}
            </RenderContext.Provider>
        );
    }

    return <RenderContext.Provider value={ctx}>{children}</RenderContext.Provider>;
};

export type { ProseImgProps, ProseRootProps, SectionProps };
export { ProseImg as Img, Root, Section, ToC };
